<template>
  <div class="messages-container col-12">
    <tabs />
    <div class="messages-header col-12">
      <span class="title">AUTOMATED MESSAGES</span>
      <div class="create-button" @click="routeToAutomatedMessages()">
        <i class="fas fa-plus" style="margin-right: 5px;" />
        <span>CREATE NEW AUTOMATED MESSAGE</span>
      </div>
    </div>
    <div class="filters-section">
      <input 
        class="search-input" 
        type="text" 
        v-model="searchFilter" 
        placeholder="Search message name..."
      />
      <div class="btn-group" role="group">
        <button
          type="button"
          :class="`btn btn-sm ${statusFilters.draft ? 'btn-success' : 'btn-secondary'}`"
          @click="toggleStatusFilter('draft')"
        >
          Draft
        </button>
        <button
          type="button"
          :class="`btn btn-sm ${statusFilters.scheduled ? 'btn-success' : 'btn-secondary'}`"
          @click="toggleStatusFilter('scheduled')"
        >
          Scheduled
        </button>
        <button
          type="button"
          :class="`btn btn-sm ${statusFilters.delivered ? 'btn-success' : 'btn-secondary'}`"
          @click="toggleStatusFilter('delivered')"
        >
          Ongoing
        </button>
        <button
          type="button"
          :class="`btn btn-sm ${statusFilters.ended ? 'btn-success' : 'btn-secondary'}`"
          @click="toggleStatusFilter('ended')"
        >
          Ended
        </button>
      </div>
    </div>
    <div class="flex1">
      <div class="row titles">
        <div class="col-md-1 col-12 text-center">
          Status
        </div>
        <div class="col-md-1 col-12">
          Message Name
        </div>
        <div class="col-md-1 col-12 text-center">
          Id
        </div>
        <div class="col-md-1 col-12 text-center sortable" @click="changeSortParams('sendTime')">
          Send At {{ getSortIcon('sendTime') }}
        </div>
        <div class="col-md-1 col-12 text-center">
          Ended At
        </div>
        <div class="col-md-1 col-12 text-center">
          Audience
        </div>
        <div class="col-md-1 col-12 text-center sortable" @click="changeSortParams('audienceCount')">
          Audience Count {{ getSortIcon('audienceCount') }}
        </div>
        <div class="col-md-1 col-12 text-center sortable" @click="changeSortParams('sent')">
          Sent {{ getSortIcon('sent') }}
        </div>
        <div class="col-md-1 col-12 text-center sortable" @click="changeSortParams('clicked')">
          Clicked {{ getSortIcon('clicked') }}
        </div>
        <div class="col-md-1 col-12 text-center sortable" @click="changeSortParams('ctr')">
          CTR {{ getSortIcon('ctr') }}
        </div>
        <div class="col-md-1 col-12 text-center">
          Actions
        </div>
      </div>
      <div class="row line-hover" v-for="message in filteredAndSortedMessages" :key="`message-${message.id}`"
        @click="$router.push({ name: 'MessagesAutomatedMessage', params: { id: message.id } })">
        <div class="col-md-1 col-12 text-center">
          <div class="badge"
            :class="message.status === 'delivered' ? 'badge-success' : (message.status === 'scheduled' ? 'badge-warning' : (message.status === 'draft' ? 'badge-secondary' : 'badge-info'))"
            style="text-transform: capitalize;">
            {{ showStatus(message.status) }}
          </div>
        </div>
        <div class="col-md-1 col-12">
          {{ message.name }}
        </div>
        <div class="col-md-1 col-12 text-center" @click.stop="copyToClipBoard(message.id)">
          {{ message.id.slice(0, 10) }}...
        </div>
        <div class="col-md-1 col-12 text-center">
          {{ dateFormatter(message.schedule.sendTime) }}
        </div>
        <div class="col-md-1 col-12 text-center">
          {{ displayEndedTime(message.schedule.endTime) }}
        </div>
        <div class="col-md-1 col-12 text-center">
          {{ message.audience.sendToAll ? 'All' : message.audience.includedAudience.map(audience =>
              audience.name).join(', ')
          }}
        </div>
        <div class="col-md-1 col-12 text-center">
          <span :class="getAudienceCountClass(message)">
            {{ getAudienceCountDisplay(message) }}
          </span>
        </div>
        <div class="col-md-1 col-12 text-center">
          {{ message && message.statistics && message.statistics.sent ? message.statistics.sent : 0 }}
        </div>
        <div class="col-md-1 col-12 text-center">
          {{ message && message.statistics && message.statistics.clicked ? message.statistics.clicked : 0 }}
        </div>
        <div class="col-md-1 col-12 text-center">
          {{ ctr(message && message.statistics && message.statistics.clicked ? message.statistics.clicked : 0, message && message.statistics && message.statistics.sent ? message.statistics.sent : 0) }}
        </div>
        <div class="col-md-1 col-12 text-center">
          <popup-menu>
            <template v-slot:trigger>
              <i class="fas fa-ellipsis-v" />
            </template>
            <template v-slot:content>
              <a style="cursor: pointer; color: white; margin: 5px 0;"
                @click.stop="$router.push({ name: 'MessagesAutomatedMessage', params: { id: message.id } })">
                <i class="fas fa-pen-square" />
                {{ message.status === 'ended' || message.status === 'delivered' ? 'View' : 'Edit & View' }}
              </a>
              <a style="cursor: pointer; color: white; margin: 5px 0;" @click.stop="() => duplicateMessage(message)">
                <i class="fas fa-copy" />
                Duplicate
              </a>
              <a style="cursor: pointer; color: red; margin: 5px 0;" @click.stop="() => deleteMessage(message)">
                <i class="fas fa-ban" />
                {{ message.status === 'scheduled' ? 'Cancel' : (message.status === 'delivered' ? 'End' : 'Delete') }}
              </a>
            </template>
          </popup-menu>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PopupMenu from "@/views/PushNotification/UIComponents/PopupMenu";
import Tabs from "@/views/PushNotification/Tabs";
import formatters from "@/util/formatters";
import notify from "@/util/notification";
import copyToClipBoard from "@/util/copyToClipboard";
import { ctr } from "@/util/ctr";
const isLocal = window.location.href.indexOf("localhost") >= 0;
const cancelBigqueryJobUrl = isLocal ? "http://localhost:5002/audienceCount/cancel-bigquery-job" : "/audienceCount/cancel-bigquery-job";

export default {
  name: "AutomatedMessages",
  components: {
    PopupMenu,
    Tabs
  },
  computed: {
    messages() {
      return this.$store.getters.automatedMessages
    },
    appId() {
      return this.$store.getters.activeApp
    },
    filteredAndSortedMessages() {
      let filtered = this.messages.filter(message => {
        const statusMatch = this.statusFilters[message.status];
        const searchMatch = message.name.toLowerCase().includes(this.searchFilter.toLowerCase());
        return statusMatch && searchMatch;
      });
      return filtered.sort(this.sorter);
    }
  },
  methods: {
    toggleStatusFilter(status) {
      this.statusFilters[status] = !this.statusFilters[status];
    },
    changeSortParams(field) {
      if (this.sortParams.field === field) {
        this.sortParams.direction = this.sortParams.direction === 'ASC' ? 'DESC' : 'ASC';
      } else {
        this.sortParams = { field, direction: 'DESC' };
      }
    },
    getSortIcon(field) {
      if (this.sortParams.field !== field) return '';
      return this.sortParams.direction === 'ASC' ? '↑' : '↓';
    },
    sorter(a, b) {
      const { field, direction } = this.sortParams;
      let aValue, bValue;

      switch(field) {
        case 'sendTime':
          aValue = a.schedule.sendTime || 0;
          bValue = b.schedule.sendTime || 0;
          break;
        case 'audienceCount':
          aValue = a.audience_count?.status === 'DONE' ? a.audience_count.value : 0;
          bValue = b.audience_count?.status === 'DONE' ? b.audience_count.value : 0;
          break;
        case 'sent':
          aValue = a.statistics?.sent || 0;
          bValue = b.statistics?.sent || 0;
          break;
        case 'clicked':
          aValue = a.statistics?.clicked || 0;
          bValue = b.statistics?.clicked || 0;
          break;
        case 'ctr':
          const aSent = a.statistics?.sent || 0;
          const aClicked = a.statistics?.clicked || 0;
          const bSent = b.statistics?.sent || 0;
          const bClicked = b.statistics?.clicked || 0;
          aValue = aSent > 0 ? (aClicked / aSent) : 0;
          bValue = bSent > 0 ? (bClicked / bSent) : 0;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return direction === 'ASC' ? -1 : 1;
      if (aValue > bValue) return direction === 'ASC' ? 1 : -1;
      return 0;
    },
    displayEndedTime(time) {
      if (time) {
        return this.dateFormatter(time)
      }
      return "-"
    },
    showStatus(messageStatus) {
      let status;
      messageStatus == "delivered" ? status = "ongoing" : status = messageStatus;
      return status;
    },
    copyToClipBoard(id) {
      copyToClipBoard(id);
    },
    routeToAutomatedMessages() {
      if (!this.canSendPushNotifications && !this.canCreateDraftMessages) {
        return notify('You are not authorized to send push notifications or create draft messages', "error")
      }
      this.$router.push({ name: 'MessagesAutomatedMessage' })
    },
    getAudienceCountDisplay(message) {
      if (!message.audience_count) return 'N/A';
      
      switch(message.audience_count.status) {
        case 'PENDING': return 'Pending...';
        case 'DONE': return message.audience_count.value;
        case 'ERROR': return 'Error';
        case 'TIMEOUT': return 'Timeout';
        default: return 'N/A';
      }
    },
    getAudienceCountClass(message) {
      if (!message.audience_count) return '';
      
      switch(message.audience_count.status) {
        case 'ERROR': return 'text-danger';
        case 'TIMEOUT': return 'text-warning';
        case 'PENDING': return 'text-info';
        default: return '';
      }
    },
    duplicateMessage(message) {
      const payload = { ...message }

      payload.name += '_copy'
      payload.status = 'draft'
      payload.cloudTaskName = undefined
      payload.schedule.sendTime = Date.now();
      delete payload.id
      payload.schedule.endTime = ""
      payload.statistics = {
        sent: 0,
        clicked: 0
      }
      if (message.audience_count) {
        delete payload.audience_count;
      }
      this.$store.dispatch('configAdd', { type: 'message', payload });
    },
    deleteMessage(message) {
      if (message.status === 'scheduled') {
        if (
          confirm(
            `You are trying to cancel "${message.name
            }" automated message. Are you sure?`
          )
        ) {
          const newMessage = { ...message }
          newMessage.status = 'draft'
          const payload = { id: message.id, data: newMessage }
          this.$store.dispatch('configEdit', { type: 'message', payload });
        }
      } else if (message.status === 'delivered') {
        if (
          confirm(
            `You are trying to end "${message.name
            }" automated message. Are you sure?`
          )
        ) {
          const newMessage = { ...message }
          newMessage.status = 'ended'
          newMessage.schedule.endTime = Date.now();
          const payload = { id: message.id, data: newMessage }
          this.$store.dispatch('configEdit', { type: 'message', payload });
        }
      } else {
        if (
          confirm(
            `You are trying to delete "${message.name
            }" message. Are you sure?`
          )
        )
          this.$store.dispatch('configDelete', { type: 'message', payload: message.id });
          // if the message have active audience count job, cancel it
          if (message.audience_count && message.audience_count.status === 'PENDING') {
            fetch(cancelBigqueryJobUrl, { method: 'POST', body: JSON.stringify({ jobId: message.audience_count.jobId, appId: this.appId }), headers: { authorization: this.$store.getters.pushNotifApiKey, "Content-Type": "application/json" } });
          }
      }
    }

  },
  data() {
    return {
      dateFormatter: formatters.dateFormatter,
      ctr,
      searchFilter: '',
      statusFilters: {
        draft: true,
        scheduled: true,
        delivered: true,
        ended: true
      },
      sortParams: {
        field: 'sendTime',
        direction: 'DESC'
      }
    }
  }

}
</script>

<style scoped>
.messages-header {
  margin: 25px 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.title {
  font-size: 24px;
}

.create-button {
  cursor: pointer;
  border-radius: 28px;
  background: #03B3DA;
  color: white;
  padding: 10px 15px;
  font-size: 14px;
}

.titles > div {
  padding-left: 5px;
  padding-right: 5px;
  font-size: 24px;
}

.filters-section {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 20px;
}

.search-input {
  padding: 5px 10px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background-color: rgba(255, 255, 255, 0.1);
  color: white;
  min-width: 200px;
}

.search-input::placeholder {
  color: rgba(255, 255, 255, 0.5);
}

.sortable {
  cursor: pointer;
  user-select: none;
}

.sortable:hover {
  opacity: 0.8;
}
</style>